import { DEFAULT_PATH } from "@/constants/paths";
import { getConfig } from "@/lib/runtimeConfig";

const DEFAULT_ALLOWED_RETURN_HOSTS = ["marketing.anarockgenie.com", "marketing.staging.anarockgenie.com"];
const INTERNAL_REDIRECT_ORIGIN = "https://anarockgenie.local";

type QueryValue = string | string[] | null | undefined;

function getAllowedReturnHosts(): string[] {
  const fromEnv = getConfig("NEXT_PUBLIC_ALLOWED_RETURN_HOSTS");
  if (!fromEnv) {
    return DEFAULT_ALLOWED_RETURN_HOSTS;
  }

  return fromEnv
    .split(",")
    .map(host => host.trim().toLowerCase())
    .filter(Boolean);
}

function getFirstQueryValue(value: QueryValue): string | null {
  if (Array.isArray(value)) {
    return value.find(Boolean) ?? null;
  }

  return value || null;
}

function hasUnsafeInternalPathPrefix(url: string): boolean {
  if (!url.startsWith("/") || url.startsWith("//") || url.includes("\\")) {
    return true;
  }

  try {
    const decoded = decodeURIComponent(url);
    return decoded.startsWith("//") || decoded.startsWith("/\\") || decoded.includes("\\");
  } catch {
    return true;
  }
}

export function getSafeInternalRedirectPath(url: QueryValue): string | null {
  const path = getFirstQueryValue(url);
  if (!path || hasUnsafeInternalPathPrefix(path)) {
    return null;
  }

  try {
    const parsed = new URL(path, INTERNAL_REDIRECT_ORIGIN);
    if (parsed.origin !== INTERNAL_REDIRECT_ORIGIN) {
      return null;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function isAllowedReturnUrl(url: QueryValue): boolean {
  const returnUrl = getFirstQueryValue(url);
  if (!returnUrl) {
    return false;
  }

  try {
    const parsed = new URL(returnUrl);
    if (parsed.protocol !== "https:") {
      return false;
    }

    return getAllowedReturnHosts().includes(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
}

interface PostLoginRedirectOptions {
  returnUrl?: QueryValue;
  rt?: QueryValue;
  query?: Record<string, string | string[] | undefined>;
}

export function resolvePostLoginRedirect({ returnUrl, rt, query = {} }: PostLoginRedirectOptions): {
  isExternal: boolean;
  url: string;
} {
  const safeReturnUrl = getFirstQueryValue(returnUrl);
  if (safeReturnUrl && isAllowedReturnUrl(safeReturnUrl)) {
    return { isExternal: true, url: safeReturnUrl };
  }

  const queryParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (key === "rt" || key === "return_url" || value === undefined) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(entry => queryParams.append(key, entry));
    } else {
      queryParams.set(key, value);
    }
  });

  let redirectTo = DEFAULT_PATH;
  const safeRt = getSafeInternalRedirectPath(rt);
  if (safeRt) {
    const parsed = new URL(safeRt, INTERNAL_REDIRECT_ORIGIN);
    queryParams.forEach((value, key) => parsed.searchParams.append(key, value));
    redirectTo = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }

  return { isExternal: false, url: redirectTo };
}

export function redirectAfterAuth(destination: { isExternal: boolean; url: string }) {
  if (destination.isExternal) {
    globalThis.location.href = isAllowedReturnUrl(destination.url) ? destination.url : DEFAULT_PATH;
    return;
  }

  const path = getSafeInternalRedirectPath(destination.url) ?? DEFAULT_PATH;
  globalThis.location.href = path;
}
